import { type ReactNode, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { type DrillDownMetric, type WorkflowCategory } from '@apm/contracts';
import { ChartFrame } from '../../../components/chart/chart-frame';
import { DrillDownPanel } from '../../../components/chart/drill-down-panel';
import { FLOW_VAR } from '../../../components/chart/rag';
import { errorMessage } from '../../../lib/error-message';
import { type ReportRange, reportApi, reportKeys } from '../api';

interface Slice {
  readonly metric: DrillDownMetric;
  readonly bucket: string;
  readonly category?: string;
}

/**
 * **Cumulative Flow Diagram** — biểu đồ quan trọng nhất của Kanban (spec 16.2).
 *
 * Vùng màu nào phình ra chính là bottleneck: dải "đang làm" dày dần nghĩa là
 * việc vào nhanh hơn việc ra, và đó là thứ không bảng số nào nói ra được.
 *
 * Dải để drill-down **chọn tường minh** bằng ô select, không đoán theo vị trí
 * chuột: một biểu đồ xếp chồng thì điểm click nằm ở dải nào là chuyện của vài
 * pixel, và mở sai danh sách còn tệ hơn không mở.
 */
export function FlowChart({
  projectId,
  range,
}: {
  projectId: string;
  range: ReportRange;
}): ReactNode {
  const { t } = useTranslation();
  const [slice, setSlice] = useState<Slice | null>(null);
  const [expired, setExpired] = useState(false);
  const [band, setBand] = useState<WorkflowCategory>('in_progress');

  const flow = useQuery({
    queryKey: reportKeys.flow(projectId, range),
    queryFn: ({ signal }) => reportApi.flow(projectId, range, signal),
    staleTime: 0,
    refetchInterval: 15_000,
  });

  const updatedAt = flow.data?.snapshot?.computedAt;
  const refreshFlow = flow.refetch;
  useEffect(() => {
    setExpired(false);
    if (!updatedAt) return;
    const timer = setTimeout(
      () => {
        setExpired(true);
        void refreshFlow();
      },
      Math.max(0, Date.parse(updatedAt) + 30_000 - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [updatedAt, refreshFlow]);

  const drill = useQuery({
    queryKey: [
      ...reportKeys.drillDown(
        projectId,
        slice?.metric ?? '',
        slice?.bucket ?? '',
        slice?.category ?? '',
        range.asOf ?? '',
      ),
      flow.data?.snapshot?.revision ?? '',
    ],
    queryFn: () =>
      reportApi.drillDown(projectId, {
        metric: slice!.metric,
        bucket: slice!.bucket,
        ...(slice!.category ? { category: slice!.category } : {}),
        ...(flow.data?.snapshot ? { snapshot: flow.data.snapshot } : {}),
        ...(range.asOf ? { asOf: range.asOf } : {}),
      }),
    enabled: slice !== null && flow.data !== undefined,
    retry: false,
  });

  const buckets = flow.data?.buckets ?? [];
  const isEmpty = buckets.every((bucket) => bucket.total === 0);

  return (
    <ChartFrame
      title={t('reports.flowTitle')}
      help="flow"
      subtitle={[
        t('reports.flowSubtitle', { count: flow.data?.eventCount ?? 0 }),
        flow.data?.snapshot
          ? t('common.updatedAt', {
              at: new Date(flow.data.snapshot.computedAt).toLocaleTimeString(),
            })
          : null,
      ]
        .filter(Boolean)
        .join(' · ')}
      asOf={range.asOf ? new Date(range.asOf) : undefined}
      loading={flow.isPending || (expired && !flow.isError)}
      error={flow.isError ? errorMessage(flow.error, t) : null}
      onRetry={() => void flow.refetch()}
      empty={
        !flow.isPending && !flow.isError && isEmpty
          ? { title: t('reports.emptyTitle'), description: t('reports.emptyHelp') }
          : null
      }
      legend={[
        { label: t('chart.category.todo'), color: FLOW_VAR.todo },
        { label: t('chart.category.in_progress'), color: FLOW_VAR.inProgress },
        { label: t('chart.category.done'), color: FLOW_VAR.done },
      ]}
      actions={
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          {t('reports.drillBand')}
          <select
            className="h-7 rounded-control border border-border-strong bg-background px-1 text-xs"
            value={band}
            onChange={(event) => {
              const next = event.target.value as WorkflowCategory;
              setBand(next);
              setSlice((current) => (current ? { ...current, category: next } : null));
            }}
          >
            {(['todo', 'in_progress', 'done'] as const).map((value) => (
              <option key={value} value={value}>
                {t(`chart.category.${value}`)}
              </option>
            ))}
          </select>
        </label>
      }
      drillDown={
        slice && !expired && !flow.isError ? (
          <DrillDownPanel
            error={drill.isError ? errorMessage(drill.error, t) : null}
            onRetry={() => {
              void flow.refetch();
            }}
            result={drill.data ?? null}
            loading={drill.isPending}
            onClose={() => setSlice(null)}
          />
        ) : null
      }
    >
      <div className="h-56 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={buckets}
            // Click vào bất kỳ đâu trên biểu đồ mở danh sách task đứng sau —
            // tính chất bắt buộc số 2 (spec 16.6).
            onClick={(state: { activeLabel?: string | number | undefined }) => {
              const bucket = state?.activeLabel;
              if (typeof bucket === 'string') {
                setSlice({ metric: 'flow_status', bucket, category: band });
              }
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="var(--color-border)" />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="var(--color-border)" />
            <Tooltip
              contentStyle={{
                background: 'var(--color-background)',
                border: '1px solid var(--color-border)',
                fontSize: 12,
              }}
            />
            {/* Xếp chồng theo thứ tự dòng chảy: chưa làm → đang làm → xong. */}
            <Area
              type="monotone"
              dataKey="todo"
              name={t('chart.category.todo')}
              stackId="flow"
              stroke={FLOW_VAR.todo}
              fill={FLOW_VAR.todo}
            />
            <Area
              type="monotone"
              dataKey="inProgress"
              name={t('chart.category.in_progress')}
              stackId="flow"
              stroke={FLOW_VAR.inProgress}
              fill={FLOW_VAR.inProgress}
            />
            <Area
              type="monotone"
              dataKey="done"
              name={t('chart.category.done')}
              stackId="flow"
              stroke={FLOW_VAR.done}
              fill={FLOW_VAR.done}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </ChartFrame>
  );
}
